import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { AuthContext } from 'contexts/AuthContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { useModal } from 'components/Modals';
import Container from 'components/Container';
import Header from 'components/Header';
import Loader from 'components/Loader';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import styles from './styles';
import moment from 'moment';
import { toastWarning } from 'utils/toast';

const Status = {
  PENDENTE: 'PENDENTE',
  CONCLUIDO: 'CONCLUIDO',
  APROVADO: 'APROVADO',
  REPROVADO: 'REPROVADO',
};

const renderAcoesAnalise = (titulo, selected, onSubmit) => {
  const { openModal } = useModal();

  if (titulo === 'Pendentes') {
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={() =>
          selected?.length > 0
            ? openModal(<ConfirmModal itens={selected} onSubmit={onSubmit} />)
            : toastWarning('Nenhum item selecionado')
        }
      >
        Analisar
      </Button>
    );
  }
  if (titulo === 'Concluídas') {
    return (
      <Button
        color="secondary"
        variant="outlined"
        onClick={() =>
          selected?.length > 0
            ? openModal(<ConfirmModal itens={selected} onSubmit={onSubmit} />)
            : toastWarning('Nenhum item selecionado')
        }
      >
        Cancelar
      </Button>
    );
  }
  return null;
};

const renderAcoesAprovacao = (titulo, selected, onSubmit) => {
  const { openModal } = useModal();
  const buttons = [];

  if (titulo === 'Pendentes') {
    buttons.push(
      <Button
        color="primary"
        variant="contained"
        onClick={() =>
          selected?.length > 0
            ? openModal(
                <AprovaModal
                  itens={selected}
                  onSubmit={onSubmit}
                  aprovado="SIM"
                />
              )
            : toastWarning('Nenhum item selecionado')
        }
      >
        Aprovar
      </Button>,
      <Button
        color="secondary"
        variant="contained"
        onClick={() =>
          selected?.length > 0
            ? openModal(
                <AprovaModal
                  itens={selected}
                  onSubmit={onSubmit}
                  aprovado="NAO"
                />
              )
            : toastWarning('Nenhum item selecionado')
        }
      >
        Reprovar
      </Button>
    );
  } else if (['Aprovadas', 'Reprovadas'].includes(titulo)) {
    buttons.push(
      <Button
        color="secondary"
        variant="outlined"
        onClick={() =>
          selected?.length > 0
            ? openModal(<ConfirmModal onSubmit={onSubmit} itens={selected} />)
            : toastWarning('Nenhum item selecionado')
        }
      >
        Cancelar
      </Button>
    );
  }
  return buttons;
};

const ConfirmModal = ({ itens, onSubmit }) => {
  const { documento, postLoading } = useContext(DocumentosContext);
  const { closeModal } = useModal();
  const isConfirm = itens?.[0]?.status === Status.PENDENTE;
  const isCancelAnalise = itens?.[0]?.status === Status.CONCLUIDO;
  const isCancelAprovacao = [Status.APROVADO, Status.REPROVADO].includes(
    itens?.[0]?.status
  );
  const title = isConfirm
    ? 'Confirmar Análise'
    : isCancelAnalise
    ? 'Cancelar Análise'
    : isCancelAprovacao
    ? 'Cancelar Aprovação'
    : '';

  const text = isConfirm
    ? 'Deseja realmente confirmar a análise?'
    : isCancelAnalise
    ? 'Deseja realmente cancelar a análise?'
    : isCancelAprovacao
    ? 'Deseja realmente cancelar a aprovação?'
    : '';

  const onConfirm = () => {
    const Itens = documento?.DocumentoItems?.map((di) => ({
      id: di?.id,
      documento_id: di?.documento_id,
      Aprovacoes: di?.DocumentoItemAprovacaos?.map((dia) => {
        if (itens?.some((s) => s?.id === dia?.id)) {
          return {
            id: dia?.id,
            documento_item_id: dia?.documento_item_id,
            ...(isCancelAnalise && { analisou_id: null, dhanalisou: null }),
            ...(isCancelAprovacao && {
              aprovou_id: null,
              dhaprovou: null,
              aprovado: null,
              acao: null,
              justificativa: null,
            }),
          };
        }
        return null;
      })?.filter(Boolean),
    }))?.filter((f) => Boolean(f?.Aprovacoes?.length));

    onSubmit({ Itens });
  };

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Warning sx={{ fontSize: 120 }} />
        <DialogContentText>{text}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button loading={postLoading} onClick={onConfirm}>
          Confirmar
        </Button>
        <Button onClick={closeModal} color="secondary">
          Cancelar
        </Button>
      </DialogActions>
    </>
  );
};

const AprovaModal = ({ itens, aprovado, onSubmit }) => {
  const defaultValues = { aprovado, acao: '', justificativa: '' };
  const { control, handleSubmit } = useForm({ defaultValues });
  const { documento, postLoading } = useContext(DocumentosContext);
  const { closeModal } = useModal();

  const onConfirm = ({ justificativa, acao, aprovado }) => {
    const Itens = documento?.DocumentoItems?.map((di) => ({
      id: di?.id,
      documento_id: di?.documento_id,
      Aprovacoes: di?.DocumentoItemAprovacaos?.map((dia) => {
        if (itens?.some((s) => s?.id === dia?.id)) {
          return {
            acao,
            aprovado,
            id: dia?.id,
            justificativa,
            documento_item_id: dia?.documento_item_id,
          };
        }
        return null;
      })?.filter(Boolean),
    }))?.filter((f) => Boolean(f?.Aprovacoes?.length));

    onSubmit({ Itens });
  };

  return (
    <>
      <DialogTitle>
        Confirmar {aprovado === 'SIM' ? 'Aprovação' : 'Reprovação'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} />
          <Grid item xs={12}>
            <Input name="acao" control={control} label="Ação" />
          </Grid>
          <Grid item xs={12}>
            <Input
              name="justificativa"
              control={control}
              label="Justificativa"
              multiline
              rows={5}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button loading={postLoading} onClick={handleSubmit(onConfirm)}>
          Confirmar
        </Button>
        <Button onClick={closeModal} color="secondary">
          Cancelar
        </Button>
      </DialogActions>
    </>
  );
};

const LinhaInfo = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box display="flex">
      <Typography flex={1} variant="caption" fontWeight="bold">
        {label}:
      </Typography>
      <Typography flex={2} variant="caption">
        {value}
      </Typography>
    </Box>
  );
};

const ListaDeItens = ({
  user,
  itens,
  titulo,
  onSelect,
  selected,
  onSubmit,
  renderAcoes,
  disableField,
}) => {
  const arr =
    selected?.length > 0
      ? selected
      : itens?.filter(
          (f) => user?.nivel === 'DIRETORIA' || f?.[disableField] === user?.id
        );
  return (
    <Card title={titulo} style={styles?.card}>
      <Box display="flex" justifyContent="center" gap={2} mb={2}>
        {renderAcoes(titulo, arr, onSubmit)}
      </Box>
      <List>
        {itens?.map((item) => (
          <ListItem key={item?.id} disablePadding divider>
            <Checkbox
              edge="start"
              onChange={() => onSelect(item)}
              checked={selected?.some((s) => s.id === item?.id)}
              disabled={
                user?.nivel !== 'DIRETORIA' && item?.[disableField] !== user?.id
              }
            />
            <Box py={1} ml={2} gap={0.5} display="flex" flexDirection="column">
              <Typography fontWeight="bold" mb={1}>
                {item?.descricao}
              </Typography>
              {item?.analisa && (
                <LinhaInfo
                  label="Responsável pela Análise"
                  value={item?.analisa}
                />
              )}
              {item?.analisou && (
                <LinhaInfo label="Analisado por" value={item?.analisou} />
              )}
              {item?.dhanalisou && (
                <LinhaInfo
                  label="Data da Análise"
                  value={moment(item?.dhanalisou).format(
                    'DD/MM/YYYY [às] HH:mm'
                  )}
                />
              )}
              {item?.aprova && (
                <LinhaInfo
                  label={
                    item?.status === 'REPROVADO'
                      ? 'Responsável pela Reprovação'
                      : 'Responsável pela Aprovação'
                  }
                  value={item?.aprova}
                />
              )}
              {item?.aprovou && (
                <LinhaInfo
                  label={
                    item?.status === 'APROVADO'
                      ? 'Aprovado por'
                      : 'Reprovado por'
                  }
                  value={item?.aprovou}
                />
              )}
              {item?.dhaprovou && (
                <LinhaInfo
                  label={
                    item?.status === 'APROVADO'
                      ? 'Data da Aprovação'
                      : 'Data da Reprovação'
                  }
                  value={moment(item?.dhaprovou).format(
                    'DD/MM/YYYY [às] HH:mm'
                  )}
                />
              )}
              {item?.motivo && (
                <LinhaInfo label="Motivo" value={item?.motivo} />
              )}
              {item?.justificativa && (
                <LinhaInfo label="Justificativa" value={item?.justificativa} />
              )}
              {item?.acao && <LinhaInfo label="Ação" value={item?.acao} />}
            </Box>
          </ListItem>
        ))}
      </List>
    </Card>
  );
};

const Aprovações = () => {
  const titulo = 'Análises e Aprovações';
  const [tab, setTab] = useState(1);
  const [selected, setSelected] = useState([]);
  const { closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { documento, getLoading, getDocumento, postDocumento } =
    useContext(DocumentosContext);

  const toggleSelect = (item) => {
    setSelected((prev) => {
      const exists = prev?.some((i) => i?.id === item?.id);
      if (exists) {
        return prev?.filter((i) => i?.id !== item?.id);
      }

      const hasDifferentStatus = prev?.some((i) => i?.status !== item?.status);
      if (hasDifferentStatus) {
        return [item];
      }

      return [...prev, item];
    });
  };

  const handleSubmit = ({ Itens }) => {
    postDocumento({
      data: { Itens },
      cb: () => {
        closeModal();
        getDocumento(documento?.id);
        setSelected([]);
      },
    });
  };

  const { analises, aprovacoes } = useMemo(() => {
    const analises = [];
    const aprovacoes = [];

    documento?.DocumentoItems?.forEach((di) => {
      di?.DocumentoItemAprovacaos?.forEach((dia) => {
        const data = {
          ...dia,
          descricao: di?.descricao,
        };

        const isAnalise = dia?.analisa_id && dia?.analisa_id !== dia?.aprova_id;
        const isAnaliseConcluida = dia?.analisou_id && dia?.dhanalisou;

        if (isAnalise) {
          const status = isAnaliseConcluida
            ? Status.CONCLUIDO
            : Status.PENDENTE;
          analises.push({ ...data, status });
        }

        const isAprovacao = dia?.aprova_id;
        const needAprovacao = !dia?.analisa_id || isAnaliseConcluida;
        const sameResponsaval = dia?.analisa_id === dia?.aprova_id;
        const isAprovado = dia?.aprovou_id && dia?.dhaprovou;

        const showAprovacao = isAprovacao && (needAprovacao || sameResponsaval);

        if (showAprovacao) {
          let status = Status.PENDENTE;
          if (isAprovado) {
            status =
              dia?.aprovado === 'SIM' ? Status.APROVADO : Status.REPROVADO;
          }
          aprovacoes.push({ ...data, status });
        }
      });
    });

    return { analises, aprovacoes };
  }, [documento]);

  useEffect(() => {
    setSelected([]);
  }, [tab]);

  if (getLoading) return <Loader />;

  return (
    <Container>
      <Header titulo={titulo} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tabs variant="fullWidth" value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value={0} label="Análises" />
            <Tab value={1} label="Aprovações" />
          </Tabs>
        </Grid>
        {tab === 0 && (
          <>
            <Grid item xs={12} md={6}>
              <ListaDeItens
                user={user}
                titulo="Pendentes"
                selected={selected}
                onSelect={toggleSelect}
                onSubmit={handleSubmit}
                disableField="analisa_id"
                renderAcoes={renderAcoesAnalise}
                itens={analises.filter((a) => a?.status === Status.PENDENTE)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ListaDeItens
                user={user}
                titulo="Concluídas"
                selected={selected}
                onSelect={toggleSelect}
                onSubmit={handleSubmit}
                disableField="analisa_id"
                renderAcoes={renderAcoesAnalise}
                itens={analises.filter((a) => a?.status === Status.CONCLUIDO)}
              />
            </Grid>
          </>
        )}
        {tab === 1 && (
          <>
            <Grid item xs={12} md={4}>
              <ListaDeItens
                user={user}
                titulo="Pendentes"
                selected={selected}
                onSelect={toggleSelect}
                onSubmit={handleSubmit}
                disableField="aprova_id"
                renderAcoes={renderAcoesAprovacao}
                itens={aprovacoes.filter((a) => a?.status === Status.PENDENTE)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ListaDeItens
                user={user}
                titulo="Aprovadas"
                selected={selected}
                onSelect={toggleSelect}
                onSubmit={handleSubmit}
                disableField="aprova_id"
                renderAcoes={renderAcoesAprovacao}
                itens={aprovacoes.filter((a) => a?.status === Status.APROVADO)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ListaDeItens
                user={user}
                titulo="Reprovadas"
                selected={selected}
                onSelect={toggleSelect}
                onSubmit={handleSubmit}
                disableField="aprova_id"
                renderAcoes={renderAcoesAprovacao}
                itens={aprovacoes.filter((a) => a?.status === Status.REPROVADO)}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
};

export default Aprovações;
